import { useEffect, useState } from 'react';
import { useKlantenStore } from '../../stores/klanten-store';

interface Props {
  isOpen: boolean;
  /** 'set' = op de blacklist zetten, 'clear' = eraf halen. */
  mode: 'set' | 'clear';
  accountName: string;
  onClose: () => void;
  onSuccess: () => void;
}

/**
 * Minimumlengte van de blacklist-reden. Spiegelbeeld van BLACKLIST_REDEN_MIN in
 * api/customers/blacklist.ts — pas beide aan, anders keurt de server af wat de
 * dialog doorlaat.
 */
const REDEN_MIN = 15;

/**
 * Bevestigingsdialog voor op/van de blacklist zetten van een Account.
 * Open voor admin en medewerker; bij 'set' is een reden van minimaal REDEN_MIN
 * tekens verplicht. De server handhaaft dezelfde regel (requireRole
 * ['admin','medewerker'] + lengtecheck) en schrijft een audit-regel met actor.
 */
export function BlacklistDialog({ isOpen, mode, accountName, onClose, onSuccess }: Props) {
  const setBlacklist = useKlantenStore((s) => s.setBlacklist);
  const [reden, setReden] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setReden('');
      setSubmitting(false);
      setError(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !submitting) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, submitting, onClose]);

  if (!isOpen) return null;

  const isSet = mode === 'set';
  const redenTeKort = isSet && reden.trim().length < REDEN_MIN;

  async function handleConfirm() {
    if (isSet && reden.trim().length < REDEN_MIN) {
      setError(
        reden.trim()
          ? `Maak de reden concreter — minimaal ${REDEN_MIN} tekens`
          : 'Reden is verplicht'
      );
      return;
    }
    setSubmitting(true);
    setError(null);

    const result = await setBlacklist(isSet, reden.trim());
    setSubmitting(false);

    if (!result.ok) {
      setError(result.error || 'Mislukt');
      return;
    }
    onSuccess();
  }

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40"
      onClick={() => { if (!submitting) onClose(); }}
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-slate-200">
          <h2 className="text-base font-semibold text-slate-900">
            {isSet ? 'Op blacklist zetten' : 'Van blacklist halen'}
          </h2>
        </div>
        <div className="px-6 py-4 space-y-3 text-sm text-slate-700">
          {isSet ? (
            <>
              <p>
                Weet je zeker dat je <span className="font-medium">{accountName}</span> op de blacklist
                wilt zetten? Nieuwe orders en offertes worden hierna geweigerd.
              </p>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  Reden <span className="text-red-600">*</span>
                </label>
                <textarea
                  value={reden}
                  onChange={(e) => setReden(e.target.value)}
                  maxLength={255}
                  rows={3}
                  autoFocus
                  placeholder="Bijv. betaalt structureel niet ondanks 3 herinneringen"
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                />
                <p className="mt-1 flex items-center justify-between text-[11px] text-slate-400">
                  <span>
                    {redenTeKort
                      ? `Nog ${REDEN_MIN - reden.trim().length} tekens — schrijf op wat er is gebeurd`
                      : 'Deze tekst ziet je collega als de klant weer belt.'}
                  </span>
                  <span>{reden.length}/255</span>
                </p>
              </div>
            </>
          ) : (
            <p>
              Weet je zeker dat je <span className="font-medium">{accountName}</span> van de blacklist
              wilt halen? Daarna kunnen er weer orders en offertes worden aangemaakt.
            </p>
          )}

          {error && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}
        </div>
        <div className="px-6 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-1.5 text-sm text-slate-600 hover:text-slate-900 disabled:opacity-50"
          >
            Annuleren
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={submitting || redenTeKort}
            className={
              isSet
                ? 'px-4 py-1.5 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 transition-colors disabled:bg-slate-300 disabled:cursor-not-allowed'
                : 'px-4 py-1.5 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 transition-colors disabled:bg-slate-300 disabled:cursor-not-allowed'
            }
          >
            {submitting
              ? 'Bezig…'
              : isSet ? 'Op blacklist zetten' : 'Van blacklist halen'}
          </button>
        </div>
      </div>
    </div>
  );
}
